/**
 * react-data-table-component styling for the admin listings.
 *
 * The library renders the title bar, sub-header, table and pagination as
 * siblings, so the "card" look is assembled by giving each band the matching
 * side borders and rounding only the first and last one.
 */
const INK = '#111827'
const BODY = '#334155'
const MUTED = '#6B7280'
const LINE = '#E9EBF0'
const LINE_SOFT = '#F1F3F7'
const WASH = '#F6F7F9'
const SURFACE = '#FFFFFF'
const BRAND = '#F4661F'
const BRAND_TINT = '#FFF1E9'

const sideBorders = {
  borderLeft: `1px solid ${LINE}`,
  borderRight: `1px solid ${LINE}`
}

export const customStyles = {
  table: {
    style: {
      backgroundColor: SURFACE
    }
  },
  // v6 calls the element around the table `tableWrapper`; `responsiveWrapper`
  // is not a customStyles key here and silently does nothing.
  tableWrapper: {
    style: {
      ...sideBorders,
      backgroundColor: SURFACE
    }
  },
  header: {
    style: {
      ...sideBorders,
      backgroundColor: SURFACE,
      color: INK,
      fontSize: '17px',
      fontWeight: 700,
      minHeight: '64px',
      paddingLeft: '20px',
      paddingRight: '16px',
      borderTop: `1px solid ${LINE}`,
      borderRadius: '16px 16px 0 0',
      overflow: 'hidden'
    }
  },
  subHeader: {
    style: {
      ...sideBorders,
      backgroundColor: SURFACE,
      minHeight: '0px',
      marginTop: 0,
      padding: '0 16px 16px 16px',
      boxShadow: 'none'
    }
  },
  headRow: {
    style: {
      backgroundColor: WASH,
      color: MUTED,
      fontSize: '13px',
      fontWeight: 600,
      minHeight: '48px',
      borderRadius: 0,
      borderTop: `1px solid ${LINE}`,
      borderBottomWidth: '1px',
      borderBottomColor: LINE,
      borderBottomStyle: 'solid',
      alignItems: 'center'
    },
    denseStyle: {
      minHeight: '40px'
    }
  },
  headCells: {
    style: {
      fontSize: '13px',
      color: MUTED,
      fontWeight: 600,
      letterSpacing: '0.01em',
      paddingLeft: '20px',
      paddingRight: '12px'
    }
  },
  cells: {
    style: {
      paddingLeft: '20px',
      paddingRight: '12px',
      color: INK
    }
  },
  rows: {
    style: {
      backgroundColor: SURFACE,
      color: BODY,
      fontSize: '14px',
      fontWeight: 400,
      minHeight: '56px',
      '&:not(:last-of-type)': {
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
        borderBottomColor: LINE_SOFT
      },
      '&:hover': {
        backgroundColor: WASH,
        transitionDuration: '.15s',
        outline: 'none'
      }
    },
    highlightOnHoverStyle: {
      backgroundColor: WASH,
      outline: 'none'
    }
  },
  expanderRow: {
    style: {
      backgroundColor: WASH,
      color: BODY
    }
  },
  noData: {
    style: {
      ...sideBorders,
      backgroundColor: SURFACE,
      color: MUTED,
      padding: '48px 20px',
      fontSize: '14px'
    }
  },
  progress: {
    style: {
      ...sideBorders,
      backgroundColor: SURFACE
    }
  },
  pagination: {
    style: {
      ...sideBorders,
      backgroundColor: SURFACE,
      color: MUTED,
      fontSize: '13px',
      minHeight: '60px',
      padding: '0 12px',
      borderTopWidth: '1px',
      borderTopColor: LINE,
      borderTopStyle: 'solid',
      borderBottom: `1px solid ${LINE}`,
      borderRadius: '0 0 16px 16px'
    },
    pageButtonsStyle: {
      borderRadius: '8px',
      height: '32px',
      width: '32px',
      padding: '4px',
      margin: '0 2px',
      cursor: 'pointer',
      transition: '.15s',
      color: MUTED,
      fill: MUTED,
      backgroundColor: 'transparent',
      '&:disabled': {
        cursor: 'unset',
        color: '#C3C9D4',
        fill: '#C3C9D4'
      },
      '&:hover:not(:disabled)': {
        backgroundColor: BRAND_TINT,
        fill: BRAND
      },
      '&:focus': {
        outline: 'none',
        backgroundColor: BRAND_TINT,
        fill: BRAND
      }
    }
  }
}

/**
 * Styles for a table with more columns than fit the content column.
 *
 * The library puts the title, sub-header and pagination outside the scrolling
 * region, so pinning a minimum width on the table itself lets the columns keep
 * their intended widths and scroll horizontally inside the card, rather than
 * being squeezed until their contents truncate.
 */
export const wideTableStyles = minWidth => ({
  ...customStyles,
  table: {
    style: {
      ...customStyles.table.style,
      minWidth
    }
  }
})
